import type { ReactElement, ReactNode } from 'react';
import { AlertCircle, ShieldCheck } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { SSOStatusResponse } from '@kirin/api-client/contracts/sso-settings';

import { IntegrationCard } from './IntegrationCard';
import { renderDefaultActions } from './defaultActions';

export interface SamlSsoIntegrationCardProps {
  status: SSOStatusResponse;
  onConnect?: () => void;
  onManage?: () => void;
  onDisconnect?: () => void;
  isLoading?: boolean;
  error?: string;
  readOnly?: boolean;
}

function renderSamlErrorAlert(error: string | undefined): ReactNode {
  if (!error) return null;
  return (
    <IntegrationCard.Body>
      <Alert data-testid="saml-error-alert" variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    </IntegrationCard.Body>
  );
}

function renderSamlErrorPill(error: string | undefined): ReactNode {
  if (!error) return null;
  return (
    <IntegrationCard.StatusBadge>
      <Badge
        data-testid="saml-error-pill"
        variant="destructive"
        className="gap-1.5"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Error
      </Badge>
    </IntegrationCard.StatusBadge>
  );
}

export function SamlSsoIntegrationCard(
  props: SamlSsoIntegrationCardProps
): ReactElement {
  const {
    status,
    onConnect,
    onManage,
    onDisconnect,
    isLoading,
    error,
    readOnly,
  } = props;
  const connected = status.isConfigured === true;

  return (
    <IntegrationCard
      icon={<ShieldCheck size={20} aria-hidden />}
      title="SAML SSO"
      subtitle="Configure SAML SSO for your organization"
      status={connected ? 'connected' : 'not_configured'}
      isLoading={isLoading}
    >
      {renderSamlErrorPill(error)}
      {renderSamlErrorAlert(error)}
      {renderDefaultActions(
        connected,
        { onConnect, onManage, onDisconnect },
        readOnly
      )}
    </IntegrationCard>
  );
}

SamlSsoIntegrationCard.displayName = 'SamlSsoIntegrationCard';
